import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import { setSystemUserPreferences } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { VIEW_TYPE_OBJ } from '@utils/MaterialsUtil';
import { printApiError, validate } from '@utils/Util';

import { PERMISSIONS, ROUTES } from '@/constants/constant';

const Login = () => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const [emailError, setEmailError] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const [passwordFieldType, setPasswordFieldType] = useState('password');

	const navigate = useNavigate();

	const login = async () => {
		if (!emailError && !passwordError) {
			if (email && password) {
				setLoading(true);
				let body = { email, password };

				try {
					const { data } = await ApiClient.POST('login', body);

					if (data.accessToken) {
						localStorage.setItem('isReload', 'true');
						localStorage.setItem('token', `${data.tokenType} ${data.accessToken}`);
						localStorage.setItem('email', email);

						toastSuccess('Successfully Logged In.');

						try {
							const { data: userInfo } = await ApiClient.GET('userInfo');
							let viewType = userInfo.systemPreference?.value
								? JSON.parse(userInfo.systemPreference.value)
								: {};

							let systemPreference = userInfo?.systemPreference?.value
								? JSON.parse(userInfo.systemPreference.value)
								: {};

							let surveySettings = userInfo.surveySettings?.value
								? JSON.parse(userInfo.surveySettings.value)
								: {};

							if (
								Object.keys(systemPreference).length > 0 &&
								systemPreference?.id &&
								systemPreference?.settingType === 'SYSTEM_PREFERENCES'
							) {
								viewType = {};
								await setSystemUserPreferences({});
							}

							const defaultSystemPreference = {
								...viewType,
								collectionDetailsViewType: viewType.collectionDetailsViewType || 'LARGE_VIEW',
								favouritePageViewType: viewType.favouritePageViewType || 'LARGE_VIEW',
								sampleDetailsViewType: viewType.sampleDetailsViewType || 'LARGE_VIEW',
								collectionDetailsPageSortType: viewType.collectionDetailsPageSortType || '',
								collectionDetailsPagePriorityType:
									viewType.collectionDetailsPagePriorityType || '',
								isShowPrice:
									viewType.isShowPrice !== undefined
										? viewType.isShowPrice
										: userInfo?.permissions?.includes(
												PERMISSIONS.COSTING_SHOW_PRICE_IN_DESIGN
											),
								COLLECTION_PAGE_TAB_VIEW:
									viewType.COLLECTION_PAGE_TAB_VIEW || 'COLLECTION_TAB',
								NITEX_PAGE_TAB_VIEW: viewType.NITEX_PAGE_TAB_VIEW || 'DESIGN_TAB',
								supplierListViewType: viewType.supplierListViewType || 'IMAGE_VIEW',
								POM_LIST_VIEW_TYPE: viewType.POM_LIST_VIEW_TYPE || 'NARROW_VIEW',
								POM_TAG_LIST_VIEW_TYPE: viewType.POM_TAG_LIST_VIEW_TYPE || 'IMAGE_VIEW',
								BLOCK_LIBRARY_LIST_TYPE: viewType.BLOCK_LIBRARY_LIST_TYPE || 'CARD_VIEW',
								moodBoardReshuffleType: systemPreference.moodBoardReshuffleType,
								moodBoardLayoutClass: systemPreference.moodBoardLayoutClass
							};
							defaultSystemPreference[VIEW_TYPE_OBJ.MATERIAL_LIST.name] = 'IMAGE_VIEW';
							userInfo.systemPreference = defaultSystemPreference;

							surveySettings = {
								COMMON_SURVEY_OPEN_DATE: surveySettings.COMMON_SURVEY_OPEN_DATE || null,
								TREND_SURVEY_OPEN_DATE: surveySettings.TREND_SURVEY_OPEN_DATE || null
							};
							userInfo.surveySettings = surveySettings;

							localStorage.setItem('userInfo', JSON.stringify(userInfo));
							localStorage.setItem('orderOfMenus', JSON.stringify(userInfo.mainMenus));

							// Redirect non-admin users to My Dashboard, admin users to their first menu
							const redirectPath = ROUTES[userInfo.mainMenus[0]];
							navigate(redirectPath);
						} catch {
							setLoading(false);
						}
					} else {
						toastError(data.message);
					}
				} catch (error) {
					printApiError(error);
					setLoading(false);
				}
			} else {
				if (!email) setEmailError('Email is required!');
				if (!password) setPasswordError('Password is required!');
			}
		}
	};

	const onChange = (e) => {
		let err = validate(e);
		if (e.target.name === 'email') {
			setEmail(e.target.value);
			setEmailError(err.value);
		} else if (e.target.name === 'password') {
			setPassword(e.target.value);
			setPasswordError(err.value);
		}
	};

	const handleKeyPress = (event) => {
		if (event.key === 'Enter') {
			login();
		}
	};

	const handlePasswordType = () => {
		setPasswordFieldType((prev) => (prev === 'password' ? 'text' : 'password'));
	};

	return (
		<LoaderComponent loading={loading}>
			<div className="entry-title d-flex align-items-center">
				<img className="pr-3" src={`${process.env.PUBLIC_URL}/icons/nitex-logo.svg`} alt="" />
				<p className="weight-500 black regular-14 pl-1">Admin Access</p>
			</div>
			<form className="registration-form login-input-forms" autoComplete="off">
				<div className="from-group mb-4">
					<label htmlFor="standard-adornment-email">Email</label>
					<input
						id="standard-adornment-email"
						placeholder="Email Address"
						type="email"
						autoComplete="off"
						name="email"
						value={email}
						onChange={onChange}
					/>
					{emailError && <p className="error">{emailError}</p>}
				</div>

				<div className="from-group password-group">
					<label htmlFor="standard-adornment-password">Password</label>
					<input
						id="standard-adornment-password"
						type={passwordFieldType}
						placeholder="Password"
						value={password}
						onChange={onChange}
						onKeyPress={handleKeyPress}
						name="password"
					/>
					{passwordError && <p className="error">{passwordError}</p>}
					<div className="eye-icon" onClick={handlePasswordType}>
						{passwordFieldType === 'password' ? (
							<img src={`${process.env.PUBLIC_URL}/icons/eye.svg`} width={20} alt="eye-icon" />
						) : (
							<img
								src={`${process.env.PUBLIC_URL}/icons/eye-off.svg`}
								width={20}
								alt="eye-icon"
							/>
						)}
					</div>
				</div>

				<div className="form-group mb-0">
					<div className="row justify-content-between">
						<div className="col-auto"></div>
						<div className="col-auto pt-3 pb-4">
							<Link
								to="/forget-password"
								className="regular-14 weight-500 gray_dark_01 text-right cursor-pointer"
							>
								Forgot password?
							</Link>
						</div>
					</div>
				</div>
				<button type="button" className="button size40 w-100" onClick={login}>
					Log in
				</button>
			</form>
		</LoaderComponent>
	);
};

export default Login;
